// End-to-end envelopes: ephemeral X25519 + HKDF-SHA-256 + AES-256-GCM, signed with the sender's Ed25519 key.
// docs/protocol.md, "Envelopes".
import { b64u, buf, concat, fromB64u, fromUtf8, ProtocolError, randomBytes, utf8 } from "./encoding.js";
import { sign, verify } from "./keys.js";
import { ENVELOPE_MAX_SKEW_MS } from "./limits.js";
const subtle = () => globalThis.crypto.subtle;
/** Canonical header bytes used as AES-GCM additional data and as the start of the signed bytes. */
export function headerBytes(h) {
    return utf8(JSON.stringify({ v: h.v, from: h.from, to: h.to, msgId: h.msgId, ctr: h.ctr, sentAt: h.sentAt, epk: h.epk }));
}
async function messageKey(shared, msgId, from, to, usage) {
    const base = await subtle().importKey("raw", shared, "HKDF", false, ["deriveKey"]);
    return subtle().deriveKey({ name: "HKDF", hash: "SHA-256", salt: buf(fromB64u(msgId)), info: buf(utf8(`whistle-env-v1|${from}|${to}`)) }, base, { name: "AES-GCM", length: 256 }, false, [usage]);
}
export async function seal(input) {
    const eph = (await subtle().generateKey({ name: "X25519" }, true, ["deriveBits"]));
    const shared = await subtle().deriveBits({ name: "X25519", public: input.recipientEncPub }, eph.privateKey, 256);
    const msgId = b64u(randomBytes(16));
    const epk = b64u(await subtle().exportKey("raw", eph.publicKey));
    const header = { v: 1, from: input.from, to: input.to, msgId, ctr: input.ctr, sentAt: input.now ?? Date.now(), epk };
    const aad = headerBytes(header);
    const iv = randomBytes(12);
    const key = await messageKey(shared, msgId, input.from, input.to, "encrypt");
    const ct = new Uint8Array(await subtle().encrypt({ name: "AES-GCM", iv: buf(iv), additionalData: buf(aad) }, key, buf(utf8(JSON.stringify(input.message)))));
    const sig = await sign(input.senderSigPriv, concat(aad, iv, ct));
    return { ...header, iv: b64u(iv), ct: b64u(ct), sig };
}
export async function open(env, input) {
    if (env.v !== 1)
        throw new ProtocolError("bad_message", "unsupported version");
    const aad = headerBytes(env);
    const iv = fromB64u(env.iv);
    const ct = fromB64u(env.ct);
    // Signature first: nothing else is trusted until it verifies.
    if (!(await verify(input.senderSigPub, env.sig, concat(aad, iv, ct))))
        throw new ProtocolError("bad_signature", "envelope signature");
    if (env.to !== input.self)
        throw new ProtocolError("wrong_recipient", "not addressed to us");
    if (!Number.isSafeInteger(env.ctr) || env.ctr <= input.lastCtr)
        throw new ProtocolError("replay", `counter ${env.ctr} <= ${input.lastCtr}`);
    const now = input.now ?? Date.now();
    if (Math.abs(now - env.sentAt) > ENVELOPE_MAX_SKEW_MS)
        throw new ProtocolError("stale", "sentAt outside the allowed window");
    const epk = await subtle().importKey("raw", buf(fromB64u(env.epk)), { name: "X25519" }, true, []);
    const shared = await subtle().deriveBits({ name: "X25519", public: epk }, input.recipientEncPriv, 256);
    const key = await messageKey(shared, env.msgId, env.from, env.to, "decrypt");
    let plain;
    try {
        plain = await subtle().decrypt({ name: "AES-GCM", iv: buf(iv), additionalData: buf(aad) }, key, buf(ct));
    }
    catch {
        throw new ProtocolError("decrypt_failed", "AES-GCM");
    }
    let message;
    try {
        message = JSON.parse(fromUtf8(new Uint8Array(plain)));
    }
    catch {
        throw new ProtocolError("bad_message", "not JSON");
    }
    return { message, ctr: env.ctr };
}
//# sourceMappingURL=envelope.js.map